import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { QueryState } from '../components/QueryState'
import { useAdminMe } from '../components/RequireAdmin'
import { ApiError } from '../lib/api'
import { adminApi, can, ORG_LIST_LIMIT, type SolutionCardInput } from '../lib/adminApi'
import { TARGET_LIFECYCLE, TARGET_SITUATION, useCommonCodes, useDongs } from '../lib/codes'

/**
 * 해결카드 등록·수정.
 *
 * **매칭 단위가 이 카드다**(설계서 M-01). 기업이 아니라 카드가 수요와 붙으므로,
 * 한 기업이 여러 일을 하면 카드를 나눠 만든다.
 *
 * **검수 상태는 여기서 안 바꾼다.** 새 카드는 항상 `draft`로 만들어지고 공개 전환은
 * 검수 화면(`/admin`)이 한다 — 등록(`card.write`)과 공개(`card.publish`)는 다른 권한이고,
 * 전이 규칙의 정본은 서버(`admin-review.ts`)다.
 *
 * 입력 항목은 `docs/10_설계/입력항목-정의표.md` §2가 정본이다. 표에 없는 항목을 만들지 않는다.
 */

function toggle(list: string[], code: string, max?: number): string[] {
  if (list.includes(code)) return list.filter((c) => c !== code)
  if (max !== undefined && list.length >= max) return list
  return [...list, code]
}

/** 빈 입력은 `null`로 보낸다 — 빈 문자열을 저장하면 "없음"과 "빈 값"이 섞인다. */
function orNull(v: string): string | null {
  const t = v.trim()
  return t === '' ? null : t
}

function numOrNull(v: string): number | null {
  return v.trim() === '' ? null : Number(v)
}

function errorMessage(error: unknown): string | null {
  if (error === null || error === undefined) return null
  if (error instanceof ApiError) {
    if (error.status === 403) return '이 작업을 할 권한이 없습니다.'
    return error.detail.length > 0 ? error.detail.join(' / ') : error.code
  }
  return '처리 중 오류가 발생했습니다.'
}

interface ChipsProps {
  codes: { code: string; label: string }[] | undefined
  selected: string[]
  onToggle: (code: string) => void
}

function Chips({ codes, selected, onToggle }: ChipsProps) {
  return (
    <div className="chips">
      {codes?.map((c) => (
        <button
          key={c.code}
          type="button"
          className={selected.includes(c.code) ? 'opt on' : 'opt'}
          onClick={() => onToggle(c.code)}
          aria-pressed={selected.includes(c.code)}
        >
          {c.label}
        </button>
      ))}
    </div>
  )
}

export function AdminCardEditPage() {
  const { id } = useParams<{ id: string }>()
  const isNew = id === undefined
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const me = useAdminMe()

  const categories = useCommonCodes('category')
  const lifecycleTargets = useCommonCodes(TARGET_LIFECYCLE)
  const situationTargets = useCommonCodes(TARGET_SITUATION)
  const resources = useCommonCodes('resource')
  const serviceAreas = useCommonCodes('service_area')
  const costTypes = useCommonCodes('cost_type')
  const pilotOptions = useCommonCodes('pilot_available')
  const dongs = useDongs()

  const orgs = useQuery({ queryKey: ['admin-orgs'], queryFn: () => adminApi.orgs({ limit: ORG_LIST_LIMIT }) })
  const card = useQuery({
    queryKey: ['admin-solution-card', id],
    queryFn: () => adminApi.solutionCard(id ?? ''),
    enabled: !isNew,
  })

  const [orgId, setOrgId] = useState(searchParams.get('orgId') ?? '')
  const [title, setTitle] = useState('')
  const [summary, setSummary] = useState('')
  const [problemSolved, setProblemSolved] = useState('')
  const [body, setBody] = useState('')
  const [categoryCodes, setCategoryCodes] = useState<string[]>([])
  const [targetCodes, setTargetCodes] = useState<string[]>([])
  const [resourceCodes, setResourceCodes] = useState<string[]>([])
  // 폐기 축(022) — 입력 UI는 없앴지만 **state와 저장 payload는 남긴다.**
  // 서버 스키마가 `.default([])`라 안 보내면 빈 배열로 덮어써 이관분·과거 카드의
  // 값이 편집 저장 한 번에 사라진다(016 함정). 읽어 온 값을 그대로 되돌려 보낸다.
  const [helpTypeCodes, setHelpTypeCodes] = useState<string[]>([])
  const [serviceAreaCodes, setServiceAreaCodes] = useState<string[]>([])
  const [serviceDongCodes, setServiceDongCodes] = useState<string[]>([])
  const [costType, setCostType] = useState('')
  const [accessCondition, setAccessCondition] = useState('')
  const [pilotAvailable, setPilotAvailable] = useState('')
  const [capacityNote, setCapacityNote] = useState('')
  const [leadTimeDays, setLeadTimeDays] = useState('')
  const [impactNote, setImpactNote] = useState('')
  const [tagText, setTagText] = useState('')
  const [sourceNote, setSourceNote] = useState('')
  const [displayOrder, setDisplayOrder] = useState('')

  // 저장하지 않은 편집 내용을 서버 응답으로 덮어쓰지 않으려고, 폼 초기화는 한 번만 한다.
  const loadedId = useRef<string | null>(null)

  useEffect(() => {
    const c = card.data
    if (c === undefined || loadedId.current === c.id) return
    loadedId.current = c.id
    setOrgId(c.org.id)
    setTitle(c.title)
    setSummary(c.summary)
    setProblemSolved(c.problemSolved)
    setBody(c.body)
    setCategoryCodes(c.categoryCodes)
    setTargetCodes(c.targetCodes)
    setResourceCodes(c.resourceCodes)
    setHelpTypeCodes(c.helpTypeCodes)
    setServiceAreaCodes(c.serviceAreaCodes)
    setServiceDongCodes(c.serviceDongCodes ?? [])
    setCostType(c.costType)
    setAccessCondition(c.accessCondition ?? '')
    setPilotAvailable(c.pilotAvailable ?? '')
    setCapacityNote(c.capacityNote ?? '')
    setLeadTimeDays(c.leadTimeDays === null ? '' : String(c.leadTimeDays))
    setImpactNote(c.impactNote ?? '')
    setTagText((c.tags ?? []).join(', '))
    setSourceNote(c.sourceNote ?? '')
    setDisplayOrder(c.displayOrder === null ? '' : String(c.displayOrder))
  }, [card.data])

  // 코드 목록이 도착하면 단일 선택 항목의 기본값을 채운다 — 값을 화면에 하드코딩하지 않는다.
  useEffect(() => {
    if (costType === '' && costTypes.data !== undefined && costTypes.data.length > 0) {
      setCostType(costTypes.data[0]?.code ?? '')
    }
  }, [costTypes.data, costType])

  // 실증 여부는 자동선택하지 않는다 — 빈 값("확인 필요")이 유효한 상태다(028, 원칙 4).
  // 첫 옵션을 미리 채우면 이관분(null) 카드를 다른 이유로 편집·저장할 때 지어낸 값이 들어간다.

  const mayWrite = can(me.data, 'card', 'write')

  const save = useMutation({
    mutationFn: async () => {
      const tags = tagText
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t !== '')
      const input: SolutionCardInput = {
        orgId,
        title: title.trim(),
        summary: summary.trim(),
        problemSolved: problemSolved.trim(),
        body: body.trim(),
        categoryCodes,
        targetCodes,
        resourceCodes,
        helpTypeCodes,
        serviceAreaCodes,
        // 제공 가능 동을 안 고르면 "동 제한 없음"이다 — 빈 배열이 아니라 null로 보낸다.
        serviceDongCodes: serviceDongCodes.length > 0 ? serviceDongCodes : null,
        costType,
        accessCondition: orNull(accessCondition),
        pilotAvailable: orNull(pilotAvailable),
        capacityNote: orNull(capacityNote),
        leadTimeDays: numOrNull(leadTimeDays),
        impactNote: orNull(impactNote),
        tags: tags.length > 0 ? tags : null,
        sourceNote: orNull(sourceNote),
        displayOrder: numOrNull(displayOrder),
      }
      if (isNew) return adminApi.createSolutionCard(input)
      return adminApi.updateSolutionCard(id, input)
    },
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-solution-cards'] })
      void queryClient.invalidateQueries({ queryKey: ['admin-solution-card', res.id] })
      void queryClient.invalidateQueries({ queryKey: ['admin-orgs'] })
      if (orgId !== '') void queryClient.invalidateQueries({ queryKey: ['admin-org', orgId] })
      if (isNew) void navigate(`/admin/cards/${res.id}`, { replace: true })
    },
  })

  const canSave =
    mayWrite &&
    orgId !== '' &&
    title.trim() !== '' &&
    summary.trim() !== '' &&
    problemSolved.trim() !== '' &&
    body.trim() !== '' &&
    categoryCodes.length > 0 &&
    targetCodes.length > 0 &&
    resourceCodes.length > 0 &&
    serviceAreaCodes.length > 0 &&
    costType !== ''

  const actionError = errorMessage(save.error)
  const backTo = orgId !== '' ? `/admin/orgs/${orgId}` : '/admin/orgs'

  return (
    <div>
      <Link className="back-link" to={backTo}>
        ← {orgId !== '' ? '기업 상세' : '기업 관리'}
      </Link>

      <div className="admin-bar">
        <div>
          <h1 className="page-title">{isNew ? '새 해결카드' : '해결카드 수정'}</h1>
          <p className="page-desc">
            {isNew
              ? '저장하면 검수 대기(작성중) 상태로 만들어집니다. 공개 전환은 검수 화면에서 합니다.'
              : '검수 상태는 이 화면에서 바꾸지 않습니다 — 검수 화면에서 전환합니다.'}
          </p>
        </div>
        {!isNew && card.data !== undefined && (
          <div className="admin-who">
            <span className="muted small">검수 상태: {card.data.reviewStatusLabel}</span>
          </div>
        )}
      </div>

      {!mayWrite && (
        <div className="notice">
          읽기 권한만 있는 계정입니다. 저장하려면 <b>card.write</b> 권한이 있어야 합니다.
        </div>
      )}

      {actionError !== null && (
        <div className="notice error" role="alert">
          {actionError}
        </div>
      )}

      {save.isSuccess && !isNew && (
        <div className="notice" role="status">
          저장했습니다.
        </div>
      )}

      <QueryState isPending={!isNew && card.isPending} error={card.error}>
        <form
          className="panel"
          onSubmit={(e) => {
            e.preventDefault()
            if (canSave) save.mutate()
          }}
        >
          <div className="field">
            <label htmlFor="card-org">
              기업 <span className="req">*</span>
            </label>
            <select id="card-org" value={orgId} onChange={(e) => setOrgId(e.target.value)}>
              <option value="">선택해 주세요</option>
              {orgs.data?.items.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
            <div className="hint">
              한 기업이 여러 일을 한다면 카드를 나눠 만듭니다 — 매칭은 카드 단위입니다.
            </div>
          </div>

          <div className="field">
            <label htmlFor="card-title">
              카드 제목 <span className="req">*</span>
            </label>
            <input
              id="card-title"
              type="text"
              value={title}
              maxLength={120}
              placeholder="예) 동네 이동 동행"
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="card-summary">
              한 줄 요약 <span className="req">*</span>
            </label>
            <input
              id="card-summary"
              type="text"
              value={summary}
              maxLength={300}
              placeholder="목록에 보이는 한 문장입니다."
              onChange={(e) => setSummary(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="card-problem">
              어떤 문제를 푸나요? <span className="req">*</span>
            </label>
            <p className="hint">
              시민이 낸 문제와 이 문장을 견주어 매칭합니다. 서비스 이름이 아니라 <b>문제</b>를
              적습니다.
            </p>
            <textarea
              id="card-problem"
              rows={4}
              value={problemSolved}
              maxLength={2000}
              onChange={(e) => setProblemSolved(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="card-body">
              상세 설명 <span className="req">*</span>
            </label>
            <textarea
              id="card-body"
              rows={8}
              value={body}
              maxLength={4000}
              onChange={(e) => setBody(e.target.value)}
            />
          </div>

          <div className="field">
            <label>
              분야 <span className="req">*</span>
            </label>
            <Chips
              codes={categories.data}
              selected={categoryCodes}
              onToggle={(c) => setCategoryCodes((v) => toggle(v, c))}
            />
          </div>

          <div className="field">
            <label>
              대상 <span className="req">*</span>
            </label>
            <p className="hint">연령대</p>
            <Chips
              codes={lifecycleTargets.data}
              selected={targetCodes}
              onToggle={(c) => setTargetCodes((v) => toggle(v, c))}
            />
            <p className="hint" style={{ marginTop: 12 }}>
              상황·특성
            </p>
            <Chips
              codes={situationTargets.data}
              selected={targetCodes}
              onToggle={(c) => setTargetCodes((v) => toggle(v, c))}
            />
          </div>

          <div className="field">
            <label>
              제공 자원 <span className="req">*</span>
            </label>
            <Chips
              codes={resources.data}
              selected={resourceCodes}
              onToggle={(c) => setResourceCodes((v) => toggle(v, c))}
            />
          </div>

          <div className="field">
            <label>
              서비스 지역 <span className="req">*</span>
            </label>
            <Chips
              codes={serviceAreas.data}
              selected={serviceAreaCodes}
              onToggle={(c) => setServiceAreaCodes((v) => toggle(v, c))}
            />
          </div>

          <div className="field">
            <label>제공 가능 동</label>
            <p className="hint">
              고르지 않으면 <b>동 제한 없음</b>으로 저장합니다. 특정 동만 되는 경우에만 고릅니다.
            </p>
            <Chips
              codes={dongs.data?.map((r) => ({ code: r.code, label: r.name }))}
              selected={serviceDongCodes}
              onToggle={(c) => setServiceDongCodes((v) => toggle(v, c))}
            />
          </div>

          <div className="field">
            <label htmlFor="card-cost">
              비용 유형 <span className="req">*</span>
            </label>
            <select id="card-cost" value={costType} onChange={(e) => setCostType(e.target.value)}>
              {costTypes.data?.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="card-pilot">실증 가능 여부</label>
            <select
              id="card-pilot"
              value={pilotAvailable}
              onChange={(e) => setPilotAvailable(e.target.value)}
            >
              <option value="">확인 필요(값 없음)</option>
              {pilotOptions.data?.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="card-access">이용 조건</label>
            <textarea
              id="card-access"
              rows={3}
              value={accessCondition}
              maxLength={1000}
              placeholder="예) 사전 신청 필요, 주 2회 한정"
              onChange={(e) => setAccessCondition(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="card-capacity">수용 규모</label>
            <input
              id="card-capacity"
              type="text"
              value={capacityNote}
              maxLength={500}
              placeholder="예) 월 10가구"
              onChange={(e) => setCapacityNote(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="card-lead">착수까지 걸리는 기간(일)</label>
            <input
              id="card-lead"
              type="number"
              min={0}
              max={3650}
              value={leadTimeDays}
              onChange={(e) => setLeadTimeDays(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="card-impact">기대 효과</label>
            <textarea
              id="card-impact"
              rows={3}
              value={impactNote}
              maxLength={1000}
              onChange={(e) => setImpactNote(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="card-tags">태그</label>
            <input
              id="card-tags"
              type="text"
              value={tagText}
              placeholder="쉼표로 구분합니다. 예) 이동, 동행, 어르신"
              onChange={(e) => setTagText(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="card-source">출처 메모 (내부용)</label>
            <p className="hint">
              이 카드를 어디서 받았는지 적어 둡니다. 공개 화면에는 나가지 않습니다.
            </p>
            <input
              id="card-source"
              type="text"
              value={sourceNote}
              maxLength={500}
              placeholder="예) 2026-08-14 발주기관 전달 원천 데이터"
              onChange={(e) => setSourceNote(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="card-order">노출 순서</label>
            <p className="hint">작은 값이 앞에 옵니다. 비우면 등록순입니다.</p>
            <input
              id="card-order"
              type="number"
              min={0}
              max={9999}
              value={displayOrder}
              onChange={(e) => setDisplayOrder(e.target.value)}
            />
          </div>

          <button className="btn" type="submit" disabled={!canSave || save.isPending}>
            {save.isPending ? '저장 중…' : '저장'}
          </button>
          {!canSave && mayWrite && (
            <p className="hint" style={{ marginTop: 8 }}>
              기업 · 제목 · 요약 · 문제 · 설명과 분야 · 대상 · 도움 방식 · 제공 자원 · 서비스 지역을
              채우면 저장할 수 있습니다.
            </p>
          )}
        </form>
      </QueryState>
    </div>
  )
}
